import EastIcon from "@mui/icons-material/East";
import FactoryOutlined from "@mui/icons-material/FactoryOutlined";
import LocalShippingOutlined from "@mui/icons-material/LocalShippingOutlined";
import WarehouseOutlined from "@mui/icons-material/WarehouseOutlined";
import { Grid, Stack } from "@mui/material";
import { DisplayOnlyField } from "components/Forms/DisplayOnlyField";
import { GetShippingDocumentQuery } from "generated-graphql/graphql";
import { ReactNode } from "react";

import { ManifestPartyCard } from "./sections/ManifestPartyCard";

type Document = NonNullable<GetShippingDocumentQuery["wasteShippingDocument"]>;

/**
 * Horizontal inset that matches `ManifestPartyCard`'s own padding. The Details
 * fields above the party row use this so their text lines up with the card's
 * text (e.g. "GENERATOR", "Facility Name") rather than with the card's outer
 * box edge.
 */
export const PARTY_CARD_INSET = 2;

/**
 * Directional separator between two party cards. A local copy of the manifest
 * drawer's arrow: that one lives inside `ManifestDetailSection` and is not
 * exported. Hidden when the row wraps to a column on small screens.
 *
 * `invisible` keeps the same footprint (so a row of fields above the cards
 * can reserve the identical width) while hiding the icon itself.
 */
export function FlowArrow({ invisible = false }: { invisible?: boolean } = {}) {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        color: "text.disabled",
        display: { xs: "none", md: "flex" },
        visibility: invisible ? "hidden" : "visible",
      }}
    >
      <EastIcon />
    </Stack>
  );
}

function GeneratorCard({ document }: { document: Document }) {
  return (
    <ManifestPartyCard title="Generator" icon={<FactoryOutlined fontSize="small" />}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <DisplayOnlyField
            label="Facility Name"
            text={document.facility?.name ?? undefined}
            copyable={false}
          />
        </Grid>
        <Grid size={12}>
          <DisplayOnlyField
            label="Generator Name"
            text={document.generatorName ?? undefined}
            copyable={false}
          />
        </Grid>
      </Grid>
    </ManifestPartyCard>
  );
}

function NamedPartyCard({ title, icon, name }: { title: string; icon: ReactNode; name: string }) {
  return (
    <ManifestPartyCard title={title} icon={icon}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <DisplayOnlyField label="Name" text={name} copyable={false} />
        </Grid>
      </Grid>
    </ManifestPartyCard>
  );
}

/**
 * The bill of lading's three parties as the manifest drawer's party cards, laid
 * out as the same directional Generator → Transporter → Destination flow the
 * shipment physically takes.
 *
 * An extracted bill of lading often carries no transporter or no destination. A
 * card whose only value is missing is dropped rather than rendered as a titled
 * empty box — the same thing the manifest's transporters card does when it has
 * no rows. Arrows are interleaved between whichever cards remain, so a dropped
 * card leaves no dangling arrow.
 */
export function ShippingDocumentParties({ document }: { document: Document }) {
  const cards: ReactNode[] = [];

  if (document.facility?.name || document.generatorName) {
    cards.push(<GeneratorCard key="generator" document={document} />);
  }
  if (document.transporterName) {
    if (cards.length > 0) cards.push(<FlowArrow key="arrow-transporter" />);
    cards.push(
      <NamedPartyCard
        key="transporter"
        title="Transporter"
        icon={<LocalShippingOutlined fontSize="small" />}
        name={document.transporterName}
      />
    );
  }
  if (document.destinationName) {
    if (cards.length > 0) cards.push(<FlowArrow key="arrow-destination" />);
    cards.push(
      <NamedPartyCard
        key="destination"
        title="Destination"
        icon={<WarehouseOutlined fontSize="small" />}
        name={document.destinationName}
      />
    );
  }

  if (cards.length === 0) return null;

  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
      {cards}
    </Stack>
  );
}
